import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Html5Qrcode } from "html5-qrcode";
import { Delete, Clock, CheckCircle2, LogIn, LogOut, Loader2, QrCode, Keyboard, X } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const KIOSK_BG = "https://images.unsplash.com/photo-1532456745301-b2c645d8b80d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1pbmltYWwlMjBhcmNoaXRlY3R1cmFsJTIwYmFja2dyb3VuZCUyMGxpZ2h0fGVufDB8fHx8MTc4MTI3MDIzNHww&ixlib=rb-4.1.0&q=85";

export default function Kiosk() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api.get(`/kiosk/${slug}`)
      .then(({ data }) => { setInfo(data); document.documentElement.style.setProperty("--brand", data.primary_color); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Quiosco no disponible"));
  }, [slug]);

  const press = (d) => { if (pin.length < 8) setPin(pin + d); };
  const back = () => setPin(pin.slice(0, -1));

  const punchPin = useCallback(async (value) => {
    if (!value || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/kiosk/${slug}/punch`, { pin: value });
      setResult({ ok: true, ...data });
      setPin("");
    } catch (e) {
      setResult({ ok: false, msg: formatApiError(e.response?.data?.detail) || "Error" });
    } finally {
      setBusy(false);
      setTimeout(() => setResult(null), 3500);
    }
  }, [slug, busy]);

  const submit = useCallback(() => { if (pin.length >= 3) punchPin(pin); }, [pin, punchPin]);

  const stopScan = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch {}
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const qr = new Html5Qrcode("qr-reader");
        scannerRef.current = qr;
        await qr.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 220 },
          async (text) => {
            const value = text.includes("PIN:") ? text.split("PIN:")[1].trim() : text.trim();
            await stopScan();
            punchPin(value);
          },
          () => {}
        );
      } catch (e) {
        setResult({ ok: false, msg: "No se pudo acceder a la cámara" });
        setScanning(false);
        setTimeout(() => setResult(null), 3500);
      }
    }, 100);
  };

  useEffect(() => () => { stopScan(); }, [stopScan]);

  if (error) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center"><div className="text-2xl font-heading font-bold">{error}</div></div>
      </div>
    );
  }
  if (!info) {
    return <div className="h-screen w-screen flex items-center justify-center bg-[#F8FAFC]"><Loader2 className="h-10 w-10 animate-spin text-slate-300" /></div>;
  }

  return (
    <div className="h-screen w-screen overflow-hidden relative flex items-center justify-center" data-testid="kiosk-screen">
      <img src={KIOSK_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-white/75 backdrop-blur-md" />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            {info.logo_url ? (
              <img src={info.logo_url} alt="logo" className="h-12 w-12 rounded-xl object-cover" />
            ) : (
              <LogoMark size={52} />
            )}
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">{info.name}</h1>
          <div className="mt-1 font-heading text-5xl font-bold tabular-nums text-slate-900">
            {clock.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <p className="text-slate-500 text-sm mt-1">{scanning ? "Escanea tu código QR" : "Introduce tu PIN o escanea tu QR"}</p>
        </div>

        {result ? (
          <div data-testid="kiosk-result" className={`rounded-3xl p-10 text-center shadow-2xl bg-white border ${result.ok ? "border-emerald-200" : "border-red-200"}`}>
            {result.ok ? (
              <>
                <div className={`mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4 ${result.action === "entrada" ? "bg-emerald-100" : "bg-amber-100"}`}>
                  {result.action === "entrada" ? <LogIn className="h-8 w-8 text-emerald-600" /> : <LogOut className="h-8 w-8 text-amber-600" />}
                </div>
                <div className="font-heading text-2xl font-bold">{result.employee_name}</div>
                <div className="text-lg mt-1 capitalize" style={{ color: "var(--brand)" }}>{result.action} registrada</div>
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mx-auto mt-3" />
              </>
            ) : (
              <div className="text-red-600 font-semibold text-lg py-6">{result.msg}</div>
            )}
          </div>
        ) : scanning ? (
          <div className="rounded-3xl bg-white/90 shadow-2xl p-6 border border-slate-200">
            <div id="qr-reader" data-testid="kiosk-qr-reader" className="rounded-xl overflow-hidden mx-auto" style={{ width: "100%" }} />
            <button onClick={stopScan} data-testid="kiosk-qr-close" className="mt-4 w-full h-12 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all flex items-center justify-center gap-2 text-slate-700 font-medium">
              <Keyboard className="h-5 w-5" /> Usar PIN
            </button>
          </div>
        ) : (
          <div className="rounded-3xl bg-white/90 shadow-2xl p-7 border border-slate-200">
            <div className="h-14 mb-5 rounded-xl bg-slate-100 flex items-center justify-center gap-3" data-testid="kiosk-pin-display">
              {pin.length === 0 ? (
                <span className="text-slate-300 text-lg">• • • •</span>
              ) : (
                pin.split("").map((_, i) => <span key={i} className="h-3.5 w-3.5 rounded-full" style={{ background: "var(--brand)" }} />)
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} data-testid={`kiosk-key-${n}`} onClick={() => press(String(n))}
                  className="h-16 rounded-2xl bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all text-2xl font-semibold text-slate-800 font-heading">
                  {n}
                </button>
              ))}
              <button data-testid="kiosk-key-back" onClick={back} className="h-16 rounded-2xl bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all flex items-center justify-center text-slate-500">
                <Delete className="h-6 w-6" />
              </button>
              <button data-testid="kiosk-key-0" onClick={() => press("0")} className="h-16 rounded-2xl bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all text-2xl font-semibold text-slate-800 font-heading">0</button>
              <button data-testid="kiosk-submit" onClick={submit} disabled={busy || pin.length < 3}
                className="h-16 rounded-2xl brand-btn flex items-center justify-center disabled:opacity-40">
                {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <CheckCircle2 className="h-7 w-7" />}
              </button>
            </div>
            <button onClick={startScan} data-testid="kiosk-scan-btn" className="mt-4 w-full h-12 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 text-slate-700 font-medium">
              <QrCode className="h-5 w-5" style={{ color: "var(--brand)" }} /> Escanear código QR
            </button>
          </div>
        )}
        <div className="text-center mt-6 text-xs text-slate-400">Modo Quiosco · Fichando</div>
      </div>
    </div>
  );
}
